import React, { useState } from 'react';
import { supabase } from '../supabase';
import { sendPushNotification, logNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

function CompletionCard({ application, isApplicant, completionMsg, isSticky, onScrollToCard, onUpdate, conversationId, session, otherPersonId, otherPerson }) {
  const trackEvent = useTrackEvent();
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);

  const priceType = completionMsg?.offer_data?.price_type;
  const price = completionMsg?.offer_data?.price;
  const isOra = priceType === 'ora';
  const agreedHours = application?.agreed_hours;

  const total = isOra && agreedHours
    ? parseFloat((parseFloat(price) * parseFloat(agreedHours)).toFixed(2))
    : parseFloat(price);

  const priceDisplay = isOra && agreedHours
    ? `€${price}/h × ${agreedHours}h = €${total.toFixed(2)}`
    : `€${price}`;

  const alreadyCompleted = application?.completed;

  const iConfirmed = isApplicant
    ? application?.confirmed_by_worker
    : application?.confirmed_by_employer;

  const handleConfirmCompletion = async () => {
    if (confirmingCompletion) return;
    setConfirmingCompletion(true);
    try {
      const updateField = isApplicant
        ? { confirmed_by_worker: true }
        : { confirmed_by_employer: true };

      const { data: updated } = await supabase
        .from('applications')
        .update(updateField)
        .eq('id', application.id)
        .select()
        .single();

      if (updated?.confirmed_by_worker && updated?.confirmed_by_employer) {
        const completedAtNow = new Date().toISOString();

        await supabase.from('applications').update({
          completed: true,
          amount_paid: total,
          completed_at: completedAtNow,
        }).eq('id', application.id);

        trackEvent('lavoro_completato', {
          conversation_id: conversationId,
          application_id: application.id,
          price,
          price_type: priceType,
          hours: isOra ? agreedHours : null,
          amount_paid: total,
        });

        const { data: transferResult, error: transferError } = await supabase.functions.invoke('transfer-to-worker', {
          body: { application_id: application.id },
        });

        if (transferError || transferResult?.error) {
          console.error('Errore transfer al lavoratore:', transferError || transferResult?.error);
        } else {
          trackEvent('pagamento_lavoratore_ricevuto', {
            conversation_id: conversationId,
            application_id: application.id,
            amount: total,
          });
        }

        await supabase.from('messages').insert([{
          conversation_id: conversationId,
          sender_id: session.user.id,
          content: `🏆 Lavoro completato! Entrambi hanno confermato — €${total.toFixed(2)}. Lascia una recensione!`,
          read: false,
          type: 'text',
        }]);
        onUpdate({ completed: true, confirmed_by_worker: true, confirmed_by_employer: true, amount_paid: total, completed_at: completedAtNow });

        if (otherPersonId) {
          sendPushNotification({
            userId: otherPersonId,
            title: '🏆 Lavoro completato!',
            body: `Entrambi avete confermato il completamento (€${total.toFixed(2)}). Lascia una recensione!`,
            url: `/chat/${conversationId}`,
          });
        }
        logNotification({
          userId: session.user.id,
          type: 'review_reminder',
          title: '⭐ Lascia una recensione',
          body: `Ricordati di recensire ${otherPerson || 'la persona'}`,
          url: `/chat/${conversationId}`,
        });
      } else {
        onUpdate(updateField);

        if (otherPersonId) {
          const roleLabel = isApplicant ? 'Il lavoratore' : 'Il committente';
          sendPushNotification({
            userId: otherPersonId,
            title: '✅ Richiesta completamento',
            body: `${roleLabel} ha confermato il completamento del lavoro. Conferma anche tu!`,
            url: `/chat/${conversationId}`,
          });
        }
      }
    } finally {
      setConfirmingCompletion(false);
    }
  };

  const renderContent = () => {
    if (alreadyCompleted) {
      return (
        <div style={{ padding: 14, textAlign: 'center', color: theme.colors.primary, fontWeight: 700, fontSize: theme.font.size.md }}>
          🏆 Lavoro completato confermato da entrambi!
        </div>
      );
    }
    return (
      <div style={{ padding: isSticky ? '10px 14px' : 14 }}>
        {!isSticky && (
          <p style={{ margin: '0 0 12px', fontSize: theme.font.size.base, color: '#555', textAlign: 'center', lineHeight: 1.5 }}>
            Quando il lavoro è terminato, entrambi dovete confermarlo per sbloccare il pagamento al lavoratore.
          </p>
        )}
        <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: isSticky ? 8 : 12, justifyContent: 'center' }}>
          {['worker', 'employer'].map(role => {
            const confirmed = role === 'worker'
              ? application?.confirmed_by_worker
              : application?.confirmed_by_employer;
            const label = role === 'worker' ? 'Lavoratore' : 'Committente';
            return (
              <span key={role} style={{
                padding: '4px 10px', borderRadius: theme.radius.pill, fontSize: theme.font.size.sm, fontWeight: 600,
                background: confirmed ? theme.colors.primaryLight : '#f5f5f5',
                color: confirmed ? theme.colors.primary : theme.colors.textMuted,
                border: `1px solid ${confirmed ? '#a5d6a7' : theme.colors.border}`
              }}>
                {confirmed ? '✅' : '⏳'} {label}
              </span>
            );
          })}
        </div>
        {iConfirmed ? (
          <div style={{ textAlign: 'center', fontSize: theme.font.size.base, color: theme.colors.primary, fontWeight: 600, padding: '6px 8px', background: theme.colors.primaryLight, borderRadius: theme.radius.sm }}>
            ✅ Hai confermato! In attesa dell'altra conferma...
          </div>
        ) : (
          <button
            onClick={isSticky ? onScrollToCard : handleConfirmCompletion}
            disabled={!isSticky && confirmingCompletion}
            style={{
              width: '100%',
              background: confirmingCompletion && !isSticky ? '#ccc' : theme.colors.primary,
              color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm,
              padding: isSticky ? 8 : 12,
              fontSize: isSticky ? theme.font.size.base : theme.font.size.lg, fontWeight: 700, cursor: 'pointer'
            }}>
            {isSticky
              ? '👆 Tocca per confermare il completamento'
              : confirmingCompletion ? 'Conferma in corso...' : '✅ Confermo che il lavoro è completato'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      {!isSticky && (
        <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '10px 14px', fontSize: theme.font.size.base, fontWeight: 700, textAlign: 'center' }}>
          🎉 Accordo raggiunto — {priceDisplay}
        </div>
      )}
      {renderContent()}
    </div>
  );
}

export default CompletionCard;